import type { SettingsTabId, SettingsTabMetadata } from '@/types/settings';

export const SETTINGS_TOOL_APPNAMES = new Set([
  'jira',
  'linear',
  'asana',
  'devrev',
  'cursor',
  'claude',
]);

export const SETTINGS_BASE_TABS: SettingsTabMetadata[] = [
  { id: 'general', label: 'General', icon: '⚙️' },
  { id: 'team', label: 'Team', icon: '👥' },
  { id: 'auth', label: 'Auth', icon: '🔐' },
  {
    id: 'code-management',
    label: 'Code Management',
    icon: '💻',
    adminOnly: true,
  },
  {
    id: 'code-review-settings',
    label: 'Code Review Settings',
    icon: '🔧',
    ownerOnly: true,
  },
  { id: 'api', label: 'API', icon: '⚡' },
  { id: 'billing', label: 'Billing', icon: '💳', ownerOnly: true },
];

export const SETTINGS_SLACK_TAB: SettingsTabMetadata = {
  id: 'slack-notification',
  label: 'Slack Notification',
  icon: '📢',
  adminOnly: true,
};

/** Sidebar search relevance keywords (client-only filtering). */
export const SETTINGS_TAB_KEYWORDS: Record<string, string[]> = {
  general: [
    'general',
    'profile',
    'account',
    'user',
    'details',
    'personal',
    'information',
    'settings',
  ],
  team: [
    'team',
    'user',
    'member',
    'people',
    'staff',
    'employee',
    'collaborator',
  ],
  auth: [
    'auth',
    'authentication',
    'token',
    'oauth',
    'github',
    'gitlab',
    'bitbucket',
  ],
  api: [
    'api',
    'key',
    'token',
    'authentication',
    'credential',
    'access',
    'secret',
    'developer',
  ],
  'code-management': [
    'codebase',
    'repository',
    'repositories',
    'repo',
    'code management',
    'pr reviews',
    'data source',
    'security scan',
    'linked repos',
  ],
  'code-review-settings': [
    'pr',
    'pull request',
    'review',
    'comment',
    'verbosity',
    'mode',
    'summary',
    'walkthrough',
    'auto',
    'scan',
    'metrics',
    'scoring',
    'guidelines',
    'learnings',
  ],
  billing: [
    'billing',
    'payment',
    'subscription',
    'plan',
    'invoice',
    'cost',
    'price',
  ],
  'slack-notification': [
    'slack',
    'notification',
    'alert',
    'message',
    'chat',
    'team communication',
  ],
};

export function settingsTabRelevance(tabId: string, query: string): number {
  if (!query.trim()) return 0;

  const searchTerms = query.toLowerCase().split(' ');
  const keywords = SETTINGS_TAB_KEYWORDS[tabId] ?? [];
  let relevance = 0;

  for (const term of searchTerms) {
    for (const keyword of keywords) {
      if (keyword.includes(term) || term.includes(keyword)) {
        relevance += 1;
      }
    }
  }

  return relevance;
}

const OWNER_ONLY_TAB_IDS = new Set(
  SETTINGS_BASE_TABS.filter((tab) => tab.ownerOnly).map((tab) => tab.id)
);

const ADMIN_ONLY_TAB_IDS = new Set(
  [...SETTINGS_BASE_TABS, SETTINGS_SLACK_TAB]
    .filter((tab) => tab.adminOnly)
    .map((tab) => tab.id)
);

export function isOwnerOnlySettingsTab(tabId: string): boolean {
  return OWNER_ONLY_TAB_IDS.has(tabId);
}

const VALID_TAB_IDS: SettingsTabId[] = [
  'general',
  'team',
  'auth',
  'code-management',
  'code-review-settings',
  'api',
  'slack-notification',
  'billing',
];

export function resolveSettingsActiveTab(
  tabParam: string | undefined,
  appname: string | undefined,
  isAdmin: boolean,
  canSeeAdminTabs: boolean = isAdmin
): SettingsTabId {
  if (appname && SETTINGS_TOOL_APPNAMES.has(appname)) {
    return 'team';
  }

  if (tabParam && isOwnerOnlySettingsTab(tabParam) && !isAdmin) {
    return 'general';
  }

  if (tabParam && ADMIN_ONLY_TAB_IDS.has(tabParam) && !canSeeAdminTabs) {
    return 'general';
  }

  if (tabParam && VALID_TAB_IDS.includes(tabParam as SettingsTabId)) {
    return tabParam as SettingsTabId;
  }

  return 'general';
}

export function connectorsRedirectPath(
  params: Record<string, string | string[] | undefined>
): string {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (key === 'tab' || value == null) continue;
    if (Array.isArray(value)) {
      value.forEach((v) => qs.append(key, v));
    } else {
      qs.set(key, value);
    }
  }
  const serialized = qs.toString();
  return serialized ? `/connectors?${serialized}` : '/connectors';
}
